import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { notificheService } from '@/services/notificheService';
import type { NotificheListParams } from '@/services/notificheService';
import { queryKeys } from '@/lib/queryKeys';
import { useAuthStore } from '@/stores/useAuthStore';
import type { Notifica, NotificheRiepilogo, Paginated, UUID } from '@/types';

export function useNotifiche(
  params: NotificheListParams = {},
  options: { enabled?: boolean } = {},
): UseQueryResult<Paginated<Notifica>> {
  const isAuthenticated = useAuthStore((state) => state.status === 'authenticated');
  const merged: NotificheListParams = {
    page: params.page ?? 1,
    pageSize: params.pageSize ?? 20,
  };
  if (params.letta !== undefined) merged.letta = params.letta;
  if (params.search) merged.search = params.search;
  if (params.categoria) merged.categoria = params.categoria;

  return useQuery({
    queryKey: queryKeys.notifiche.list(merged),
    queryFn: () => notificheService.list(merged),
    enabled: isAuthenticated && (options.enabled ?? true),
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  });
}

export function useNotificheRiepilogo(): UseQueryResult<NotificheRiepilogo> {
  const isAuthenticated = useAuthStore((state) => state.status === 'authenticated');

  return useQuery({
    queryKey: queryKeys.notifiche.riepilogo,
    queryFn: () => notificheService.riepilogo(),
    enabled: isAuthenticated,
    refetchInterval: 30_000,
  });
}

/**
 * Badge counter. Pushed by the `notification.created` socket event.
 * The interval is a fallback, short enough to catch a reminder sent one minute before.
 */
export function useUnreadCount(): UseQueryResult<{ count: number }> {
  const isAuthenticated = useAuthStore((state) => state.status === 'authenticated');

  return useQuery({
    queryKey: queryKeys.notifiche.unreadCount,
    queryFn: () => notificheService.unreadCount(),
    enabled: isAuthenticated,
    refetchInterval: 30_000,
  });
}

export function useMarkNotificaRead(): UseMutationResult<Notifica, Error, UUID, { previousCount?: { count: number } }> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: UUID) => notificheService.markRead(id),

    // Optimistic: the badge must drop the instant the user opens an item.
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.notifiche.unreadCount });
      const previousCount = queryClient.getQueryData<{ count: number }>(queryKeys.notifiche.unreadCount);

      if (previousCount) {
        queryClient.setQueryData(queryKeys.notifiche.unreadCount, { count: Math.max(0, previousCount.count - 1) });
      }

      queryClient.setQueriesData<Paginated<Notifica>>({ queryKey: [...queryKeys.notifiche.all, 'list'] }, (page) => {
        if (!page) return page;
        return {
          ...page,
          data: page.data.map((row) => (row.id === id ? { ...row, letta: true } : row)),
        };
      });

      return { previousCount };
    },

    onError: (_error, _id, context) => {
      if (context?.previousCount) {
        queryClient.setQueryData(queryKeys.notifiche.unreadCount, context.previousCount);
      }
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifiche.all });
    },

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifiche.all });
    },
  });
}

export function useMarkAllNotificheRead(): UseMutationResult<{ updated: number }, Error, void> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => notificheService.markAllRead(),
    onSuccess: () => {
      queryClient.setQueryData(queryKeys.notifiche.unreadCount, { count: 0 });
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifiche.all });
    },
  });
}
